import type { ImpiantoSintesi, Impianto } from '@emacomfort/domain';
import type { DeviceClient, ParcoClient } from './DeviceClient';
import { SimulatorClient, type OpzioniSimulatore, type ScenarioIniziale } from './SimulatorClient';

export interface ImpiantoDelParco {
  impianto: Impianto;
  scenario?: ScenarioIniziale;
}

/**
 * Parco impianti simulato: un SimulatorClient per impianto, creato alla prima
 * apertura e tenuto vivo finché il parco esiste.
 */
export class SimulatorParco implements ParcoClient {
  readonly tenantId: string;
  private readonly definizioni: Map<string, ImpiantoDelParco>;
  private readonly clients = new Map<string, SimulatorClient>();
  private readonly opzioni: Omit<OpzioniSimulatore, 'impianto' | 'scenario'>;

  constructor(tenantId: string, impianti: ImpiantoDelParco[], opzioni: Omit<OpzioniSimulatore, 'impianto' | 'scenario'> = {}) {
    this.tenantId = tenantId;
    this.definizioni = new Map(impianti.map((d) => [d.impianto.id, d]));
    this.opzioni = { registroSessioni: new Map(), ...opzioni };
  }

  get impiantoPredefinitoId(): string {
    return this.definizioni.keys().next().value as string;
  }

  async listaImpianti(): Promise<ImpiantoSintesi[]> {
    return [...this.definizioni.keys()].map((id) => this.simulatorePer(id).sintesi());
  }

  clientPer(impiantoId: string): DeviceClient {
    const esistente = this.clients.get(impiantoId);
    if (esistente) return esistente;
    const def = this.definizioni.get(impiantoId);
    if (!def) throw new Error(`impianto sconosciuto: ${impiantoId}`);
    const client = new SimulatorClient({ ...this.opzioni, impianto: def.impianto, scenario: def.scenario });
    client.avvia();
    this.clients.set(impiantoId, client);
    return client;
  }

  simulatorePer(impiantoId: string): SimulatorClient {
    return this.clientPer(impiantoId) as SimulatorClient;
  }

  ferma(): void {
    this.clients.forEach((c) => c.ferma());
    this.clients.clear();
  }
}
